import { useSuspenseQuery } from '@tanstack/react-query';
import * as React from 'react';

import type * as api from '@gitbook/api-client';
import {
    Picker,
    type PickerItemData,
    type PickerKind,
    type PickerSize,
} from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import { type IconDefinition, LoadingIndicator } from '@gitbook/spine-react';

import { apiQuery } from '../../state';
import { SpaceIcon } from '..';

type SiteSpacePickerProps = {
    /** The picker can be used as a filter or a button */
    kind?: PickerKind;
    /** The spaces list for the picker palette */
    siteId: api.Site['id'];
    /** The spaces list for the picker palette */
    organizationId: api.Organization['id'];
    /** When the picker is a button it might have a leading icon */
    leadingIcon?: IconDefinition;
    /**
     * When the picker is a button we don't show the selected space.
     */
    selectedId?: string | undefined | null;
    onSelect: (value: string | undefined) => void | Promise<void>;
    size?: PickerSize;
    className?: string;
    allowReset?: boolean;
    placeholder?: string;
    withSearch?: boolean;
    disabled?: boolean;
};

/**
 * Picker to select a site space.
 */
export function SiteSpacePicker({
    leadingIcon,
    placeholder,
    siteId,
    organizationId,
    selectedId,
    onSelect,
    allowReset = false,
    size = 'medium',
    withSearch = false,
    disabled = false,
    className,
}: SiteSpacePickerProps) {
    const hasSelected = !!selectedId;
    const [loading, setLoading] = React.useState(false);

    const {
        data: { items: siteSpaces },
    } = useSuspenseQuery(
        apiQuery('listSiteSpaces', {
            organizationId,
            siteId,
        })
    );

    const siteSpaceItems = React.useMemo<PickerItemData[]>(
        () => [
            ...(allowReset && hasSelected
                ? [
                      {
                          label: 'Clear space',
                          value: '',
                          icon: Icon.X,
                      },
                  ]
                : []),
            {
                label: 'Site-wide',
                value: 'site',
                icon: Icon.Globe,
            },
            ...siteSpaces.map(({ space, id }) => ({
                label: space.title,
                value: id,
                icon: <SpaceIcon space={space} withBackground={false} />,
            })),
        ],
        [allowReset, hasSelected, siteSpaces]
    );

    const handleChange = (value: string | undefined) => {
        const result = onSelect(value === 'site' ? undefined : value);
        if (result) {
            setLoading(true);
            result.finally(() => setLoading(false));
        }
    };

    return (
        <Picker
            leadingIcon={loading ? LoadingIndicator : leadingIcon}
            value={selectedId}
            items={siteSpaceItems}
            size={size}
            onValueChange={handleChange}
            placeholder={placeholder}
            searchLabel={withSearch ? 'Select site space…' : undefined}
            search={withSearch}
            disabled={loading || disabled}
            className={className}
        />
    );
}
